import { Button } from "@mantine/core"
import { DisabledReason, HelpButtonGroup, nextStepToast } from "@dehub/theme"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { OrderApi } from "../../apis"
import type { IOrder } from "../../models/Order"
import { usePermission } from "../permission"
import { CancelConfirmedOrderModal } from "./CancelConfirmedOrderModal"

type Props = {
  order: IOrder
  onSuccess?: () => void
  /** Help key namespace of the hosting screen, e.g. "order.supplier-sales-detail" (@dehub/help). */
  helpNs?: string
}

export function CancelConfirmedOrderButton ({ order, onSuccess, helpNs = "order.supplier-sales-detail" }: Props) {
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_CANCEL = usePermission("ORD_CANCEL")
  const [opened, setOpened] = useState(false)
  const [loading, setLoading] = useState(false)

  const statusApplies = order.orderStatus === "AUTHORIZED"
  const enabled = ORD_CANCEL.hasAccess && statusApplies

  const { data } = useSWR(
    enabled ? ["order.cancellability", order.id] : null,
    async () => await OrderApi.getCancellability(order.id as string)
  )

  if (!statusApplies) return null
  if (!ORD_CANCEL.hasAccess) {
    // Stays visible, aria-disabled, with «Эрх хэрэгтэй: ORD_CANCEL».
    return (
      <DisabledReason helpKey={`${helpNs}.reason.cancel`} reason="PERM" when>
        <Button variant="default" title={ORD_CANCEL.reason ?? undefined}>{t("supplierSales:cancelConfirmed.button")}</Button>
      </DisabledReason>
    )
  }

  const cancellable = data?.cancellable === true
  const reasons = data?.reasons ?? []
  const reasonText = reasons.length > 0
    ? reasons.map((code) => t(`supplierSales:cancelConfirmed.reasons.${code}`)).join("; ")
    : t("supplierSales:cancelConfirmed.disabled")

  const orderCode = order.salesCode || order.purchaseCode || order.id || "—"
  const invoiceCodes = [
    (order as { proformaInvoice?: { refCode?: string } }).proformaInvoice?.refCode,
    order.proformaInvoiceId,
    order.invoiceId
  ].filter((v, i, a): v is string => Boolean(v) && a.indexOf(v) === i)

  const dnCount = Array.isArray((order as { deliveryNotes?: unknown[] }).deliveryNotes)
    ? ((order as { deliveryNotes?: unknown[] }).deliveryNotes?.length ?? 0)
    : ((order as { deliveryNote?: unknown }).deliveryNote ? 1 : 0)

  const handleConfirm = async (reason: string) => {
    setLoading(true)
    try {
      await OrderApi.cancelConfirmed(order.id as string, reason)
      nextStepToast(`${helpNs}.toast.cancel`, {
        vars: { code: String(orderCode) },
        fallback: { title: t("supplierSales:cancelConfirmed.success") },
      })
      setOpened(false)
      onSuccess?.()
    } catch {
      /* HttpRequest / HttpHandler surfaces the error */
    } finally {
      setLoading(false)
    }
  }

  const button = (
    <Button
      data-help-id="action-cancel"
      variant="default"
      disabled={!cancellable}
      title={cancellable ? undefined : reasonText}
      onClick={() => setOpened(true)}
    >
      {t("supplierSales:cancelConfirmed.button")}
    </Button>
  )

  return (
    <>
      {/* Шалтгаан popover: backend reason code → help body.reasons[code] (R4); идэвхтэй үед «?» (R2) */}
      <DisabledReason helpKey={`${helpNs}.reason.cancel`} reason={reasons[0] ?? "NOT_AUTHORIZED"} when={!cancellable}>
        <HelpButtonGroup helpKey={`${helpNs}.action.cancel`}>{button}</HelpButtonGroup>
      </DisabledReason>
      <CancelConfirmedOrderModal
        opened={opened}
        onClose={() => setOpened(false)}
        onConfirm={handleConfirm}
        loading={loading}
        orderCode={String(orderCode)}
        deliveryNoteCount={dnCount}
        invoiceCodes={invoiceCodes}
        helpNs={helpNs}
      />
    </>
  )
}
